<template>
  <div>
    <div class="flex items-center justify-between px-10 py-8 bg-gray-100 border-b rounded-t-lg">
      <h1 class="text-xl font-semibold tracking-wider text-gray-800 uppercase">
        {{ $t('pages.lead.title') }}
      </h1>
      <router-link
        :to="`/companies/${lead.company.id}`"
        class="font-semibold text-gray-600 uppercase hover:text-gray-800"
      >
        {{ $t('pages.lead.backToCompany', { name: lead.company.name }) }}
      </router-link>
    </div>

    <div class="p-10">
      <div class="flex justify-between mb-32">
        <div class="w-1/2 pr-4">
          <div class="flex py-2 border-b-2">
            <div class="w-1/3 font-semibold text-gray-800 uppercase">
              {{ $t('pages.lead.fields.email') }}
            </div>
            <div class="w-2/3 text-right">
              {{ lead.email }}
            </div>
          </div>
          <div class="flex py-2 border-b-2">
            <div class="w-1/3 font-semibold text-gray-800 uppercase">
              {{ $t('pages.lead.fields.name') }}
            </div>
            <div class="w-2/3 text-right">
              {{ lead.firstname }} {{ lead.lastname }}
            </div>
          </div>
          <div class="flex items-center py-2 border-b-2">
            <div class="w-1/3 font-semibold text-gray-800 uppercase">
              {{ $t('pages.lead.fields.emailConfirmed') }}
            </div>
            <div class="w-2/3 text-right">
              {{ formatDateTime(lead.confirmedAt) }}
            </div>
          </div>
          <div class="flex items-center py-2 border-b-2">
            <div class="w-1/3 font-semibold text-gray-800 uppercase">
              {{ $t('pages.lead.fields.pendingSubscriptions') }}
            </div>
            <div class="flex flex-wrap items-center justify-end w-2/3">
              <div
                v-for="pendingSubscription in pendingSubscriptions"
                :key="pendingSubscription.id"
                class="px-4 py-1 mb-2 ml-2 font-semibold text-red-700 bg-red-200 rounded-full"
              >
                {{ pendingSubscription.campaign.name }}
              </div>
            </div>
          </div>
          <div class="flex items-center py-2 border-b-2">
            <div class="w-1/3 font-semibold text-gray-800 uppercase">
              {{ $t('pages.lead.fields.activeSubscriptions') }}
            </div>
            <div class="flex flex-wrap items-center justify-end w-2/3">
              <div
                v-for="subscription in activeSubscriptions"
                :key="subscription.id"
                class="px-4 py-1 mb-2 ml-2 font-semibold text-green-700 bg-green-200 rounded-full"
              >
                {{ subscription.campaign.name }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="lead.conversions.length"
        class="flex flex-col w-full -mt-4"
      >
        <h2 class="mt-4 text-xl font-semibold text-gray-800 uppercase">
          {{ $t('pages.lead.conversions.title') }}
        </h2>
        <div class="flex py-3 text-gray-700 uppercase border-b-4">
          <div class="w-1/2 font-semibold">
            {{ $t('pages.lead.conversions.source') }}
          </div>
          <div class="w-1/2 font-semibold">
            {{ $t('pages.lead.conversions.createdAt') }}
          </div>
        </div>
        <div
          v-for="(conversion, index) in lead.conversions"
          :key="conversion.id"
          :class="{ 'bg-gray-200': index % 2 }"
        >
          <div
            v-if="conversion.type === 'RETURN_CALL'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.returnCall') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.firstname') }}</span>
                  {{ conversion.personalData.firstname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.lastname') }}</span>
                  {{ conversion.personalData.lastname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.phone') }}</span>
                  {{ conversion.personalData.phone }}
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold"> {{ $t('pages.lead.conversions.fields.topics') }}</span>
                  <span
                    v-for="topic in conversion.returnCall.topics"
                    :key="topic"
                  >
                    {{ getReadableTopic(topic, conversion.returnCall.topicsOther) }}
                  </span>
                </div>
                <div class="mt-4">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.period') }}</span>
                  <span
                    v-for="time in conversion.returnCall.callbackTimeOfDay"
                    :key="time"
                  >
                    {{ getReadableCallbackTime(time) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="conversion.type === 'LIFE_ANNUITY'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.lifeAnnuity') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.firstname') }}</span>
                  {{ conversion.personalData.firstname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.lastname') }}</span>
                  {{ conversion.personalData.lastname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.phone') }}</span>
                  {{ conversion.personalData.phone }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.gender') }}</span>
                  {{ conversion.personalData.gender }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.yearOfBirth') }}</span>
                  {{ conversion.personalData.yearOfBirth }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.callbackTime') }}</span>
                  <span
                    v-for="time in conversion.lifeAnnuity.callbackTimeOfDay"
                    :key="time"
                  >
                    {{ getReadableCallbackTime(time) }}
                  </span>
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.buildingType') }}</span>
                  {{ conversion.lifeAnnuity.property.type }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.buildingYear') }}</span>
                  {{ conversion.lifeAnnuity.property.buildingYear }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.livingArea') }}</span>
                  {{ conversion.lifeAnnuity.property.livingArea }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.garageAmount') }}</span>
                  {{ conversion.lifeAnnuity.property.garageAmount }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.value') }}</span>
                  {{ conversion.lifeAnnuity.property.value }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.postcode') }}</span>
                  {{ conversion.lifeAnnuity.property.postcode }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.selfOccupied') }}</span>
                  {{ conversion.lifeAnnuity.property.selfOccupied }}
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.payoutType') }}</span>
                  {{ conversion.lifeAnnuity.payoutType }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="conversion.type === 'PARTIAL_SALE'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.partialSale') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.firstname') }}</span>
                  {{ conversion.personalData.firstname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.lastname') }}</span>
                  {{ conversion.personalData.lastname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.phone') }}</span>
                  {{ conversion.personalData.phone }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.callbackTime') }}</span>
                  <span
                    v-for="time in conversion.partialSale.callbackTimeOfDay"
                    :key="time"
                  >
                    {{ getReadableCallbackTime(time) }}
                  </span>
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.buildingType') }}</span>
                  {{ conversion.partialSale.property.type }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.buildingYear') }}</span>
                  {{ conversion.partialSale.property.buildingYear }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.livingArea') }}</span>
                  {{ conversion.partialSale.property.livingArea }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.garageAmount') }}</span>
                  {{ conversion.partialSale.property.garageAmount }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.value') }}</span>
                  {{ conversion.partialSale.property.value }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.postcode') }}</span>
                  {{ conversion.partialSale.property.postcode }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.selfOccupied') }}</span>
                  {{ conversion.partialSale.property.selfOccupied }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="conversion.type === 'PROPERTY_REQUEST'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.propertyRequest') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.firstname') }}</span>
                  {{ conversion.personalData.firstname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.lastname') }}</span>
                  {{ conversion.personalData.lastname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.phone') }}</span>
                  {{ conversion.personalData.phone }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.livingSituation') }}</span>
                  {{ LivingSituationLabels[conversion.propertyRequest.currentLivingSituation] }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.timeframe') }}</span>
                  {{ AvailableStartDateLabels[conversion.propertyRequest.availableStartDate] }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.financing') }}</span>
                  {{ FinancingLabels[conversion.propertyRequest.financing] }}
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.minRooms') }}</span>
                  {{ conversion.propertyRequest.property.minNumberOfRooms }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.minBathrooms') }}</span>
                  {{ conversion.propertyRequest.property.minNumberOfBathrooms }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.minParkingSpaces') }}</span>
                  {{ conversion.propertyRequest.property.minNumberOfParkingSpaces }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.accessibilityProp') }}</span>
                  {{ AccessibilityLabels[conversion.propertyRequest.property.accessibility] }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.location') }}</span>
                  {{ conversion.propertyRequest.property.location.description }} {{ $t('pages.lead.conversions.locationRadius', { radius: conversion.propertyRequest.property.location.radius }) }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.surroundings') }}</span>
                  {{ conversion.propertyRequest.property.surroundings.map((x) => SurroundingsLabels[x]).join(", ") }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.residentialArea') }}</span>
                  {{ ResidentialAreaLabels[conversion.propertyRequest.property.residentialArea] }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.price') }}</span>
                  {{ conversion.propertyRequest.property.minPrice }} - {{ conversion.propertyRequest.property.maxPrice }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="conversion.type === 'VALUATION'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.valuation') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.firstname') }}</span>
                  {{ conversion.personalData.firstname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.lastname') }}</span>
                  {{ conversion.personalData.lastname }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.phone') }}</span>
                  {{ conversion.personalData.phone }}
                </div>
                <div class="mb-2" />
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.reason') }}</span>
                  {{ getReadableReason(conversion.valuation.reason) }}
                </div>

                <div class="mb-2" />

                <div
                  v-if="hasResults(conversion.valuation)"
                  class="font-semibold"
                >
                  {{ $t('pages.lead.conversions.fields.calculatedPrices') }}
                </div>
                <div v-if="hasResults(conversion.valuation)">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.average') }}</span>
                  {{
                    formatPrice(conversion.valuation.results.property.value, conversion.valuation.results.currency)
                  }}
                </div>
                <div v-if="hasResults(conversion.valuation)">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.minimum') }}</span>
                  {{
                    formatPrice(conversion.valuation.results.property.valueRange.min, conversion.valuation.results.currency)
                  }}
                </div>
                <div v-if="hasResults(conversion.valuation)">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.maximum') }}</span>
                  {{
                    formatPrice(conversion.valuation.results.property.valueRange.max, conversion.valuation.results.currency)
                  }}
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.provider') }}</span>
                  {{ conversion.valuation.provider }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.plotType') }}</span>
                  {{ getReadablePropertyType(conversion.valuation.inputs.type) }}
                </div>
                <div v-show="conversion.valuation.inputs.subType">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.subType') }}</span>
                  {{ getReadablePropertySubType(conversion.valuation.inputs.subType) }}
                </div>
                <div v-if="conversion.valuation.inputs.address">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.address') }}</span>
                  {{ conversion.valuation.inputs.address.street }}
                  {{ conversion.valuation.inputs.address.houseNumber }},
                  {{ conversion.valuation.inputs.address.zip }}
                  {{ conversion.valuation.inputs.address.city }}
                </div>
                <br>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.buildingYear') }}</span>
                  {{ conversion.valuation.inputs.buildingYear }}
                </div>
                <div v-if="conversion.valuation.inputs.type !== PropertyType.MULTI_FAMILY_HOUSE">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.numberOfRooms') }}</span>
                  {{ conversion.valuation.inputs.numberOfRooms }}
                </div>
                <div v-if="conversion.valuation.inputs.type === PropertyType.MULTI_FAMILY_HOUSE">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.numberOfUnits') }}</span>
                  {{ conversion.valuation.inputs.numberOfUnits }}
                </div>
                <div v-if="conversion.valuation.inputs.type === PropertyType.MULTI_FAMILY_HOUSE">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.annualRentIncome') }}</span>
                  {{ conversion.valuation.inputs.annualRentIncome }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.livingAreaInput') }}</span>
                  {{ conversion.valuation.inputs.livingArea }}
                </div>
                <div v-if="conversion.valuation.inputs.landArea">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.landArea') }}</span>
                  {{ conversion.valuation.inputs.landArea }}
                </div>
                <div v-if="conversion.valuation.inputs.balconyArea">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.balconyArea') }}</span>
                  {{ conversion.valuation.inputs.balconyArea }}
                </div>
                <div v-if="conversion.valuation.inputs.quality">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.quality') }}</span>
                  {{ getReadableQuality(conversion.valuation.inputs.quality) }}
                </div>
                <div v-if="conversion.valuation.inputs.condition">
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.condition') }}</span>
                  {{ getReadableCondition(conversion.valuation.inputs.condition) }}
                </div>
                <div
                  v-if="conversion.valuation.results"
                  class="mt-4"
                >
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.valuation') }}</span>
                  <a
                    :href="getValuationPreviewUrl(conversion)"
                    class="underline"
                    target="_blank"
                  >{{ $t('pages.lead.conversions.preview') }}</a>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="conversion.type === 'PLOT_VALUATION'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.plotValuation') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.plotType') }}</span>
                  {{ getReadablePlotType(conversion.plotValuation.type) }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.area') }}</span>
                  {{ conversion.plotValuation.landArea }} m²
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.developed') }}</span>
                  {{ getReadablePlotCondition(conversion.plotValuation.conditions) }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.developmentOptions') }}</span>
                  {{ getReadablePlotDevelopment(conversion.plotValuation.development) }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.accessibility') }}</span>
                  {{ getReadableCallbackTime(conversion.plotValuation.callbackTimeOfDay) }}
                </div>
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.address') }}</span>
                  {{ conversion.plotValuation.address.street }}
                  {{ conversion.plotValuation.address.streetNumber }},
                  {{ conversion.plotValuation.address.postcode }}
                  {{ conversion.plotValuation.address.city }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="conversion.type === 'DOWNLOAD'"
            class="pb-3"
          >
            <div class="flex py-3">
              <div class="w-1/2">
                {{ $t('pages.lead.conversions.download') }} ({{ conversion.origin }})
              </div>
              <div class="w-1/2">
                {{ formatDateTime(conversion.createdAt) }}
              </div>
            </div>
            <div class="flex w-full">
              <div class="w-1/2">
                <div>
                  <span class="font-semibold">{{ $t('pages.lead.conversions.fields.email') }}</span>
                  {{ conversion.personalData.email }}
                </div>
              </div>
              <div class="w-1/2">
                <div>
                  <span class="font-semibold"> {{ $t('pages.lead.conversions.fields.downloadSource') }}</span>
                  {{ conversion?.landingpage?.slug }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex mt-24">
        <button
          class="btn-danger"
          @click="showModal('delete')"
        >
          {{ $t('pages.lead.deleteLead') }}
        </button>
        <button
          class="ml-4 btn-secondary"
          :disabled="!hasSubscriptions"
          @click="showModal('unsubscribe')"
        >
          {{ $t('pages.lead.stopFunnels') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import GET_LEAD from '@/graphql/GetLead.gql'
import DeleteModal from '@/components/modals/lead/delete'
import UnsubscribeModal from '@/components/modals/lead/unsubscribe'
import { readablePropertyType, readablePropertySubType } from '@/lib/propertyTypes'
import { readableCallbackTime, readableTopic } from '../lib/returnCallEnums'
import { readableCondition, readableQuality, readableReason } from '../lib/valuationEnums'
import { readablePlotType, readablePlotDevelopment, readablePlotCondition } from '../lib/plotValuationEnums'
import PropertyType from '@/components/leads/enums/valuation/PropertyType'
import {
  AccessibilityLabels,
  AvailableStartDateLabels,
  FinancingLabels,
  LivingSituationLabels,
  ResidentialAreaLabels,
  SurroundingsLabels
} from '@/lib/propertyRequest'

const modals = {
  delete: DeleteModal,
  unsubscribe: UnsubscribeModal
}

export default {
  apollo: {
    lead: {
      query: GET_LEAD,
      variables () {
        return {
          id: this.$route.params.leadId
        }
      }
    }
  },
  data () {
    return {
      lead: {
        landingpage: {},
        company: {},
        subscriptions: [],
        conversions: []
      },
      PropertyType,
      AccessibilityLabels,
      AvailableStartDateLabels,
      FinancingLabels,
      LivingSituationLabels,
      ResidentialAreaLabels,
      SurroundingsLabels
    }
  },
  computed: {
    activeSubscriptions () {
      return this.lead.subscriptions && this.lead.subscriptions.filter(sub => sub.subscribedAt && !sub.unsubscribedAt)
    },
    pendingSubscriptions () {
      return this.lead.subscriptions && this.lead.subscriptions.filter(sub => !sub.subscribedAt)
    },
    hasSubscriptions () {
      return this.lead.subscriptions && Boolean(this.lead.subscriptions.length)
    }
  },
  methods: {
    hasResults (valuation) {
      return valuation.provider !== 'MANUAL'
    },
    getReadablePropertyType (type) {
      return readablePropertyType[type]
    },
    getReadablePropertySubType (subType) {
      return readablePropertySubType[subType]
    },
    getReadablePlotType (type) {
      return readablePlotType[type]
    },
    getReadablePlotDevelopment (value) {
      return readablePlotDevelopment[value]
    },
    getReadableCallbackTime (value) {
      return readableCallbackTime[value]
    },
    getReadablePlotCondition (conditions) {
      return Object.entries(conditions).filter(([key, value]) => value).map(([key]) => (readablePlotCondition[key])).join(', ')
    },
    getReadableCondition (condition) {
      return readableCondition[condition]
    },
    getReadableQuality (quality) {
      return readableQuality[quality]
    },
    getReadableReason (reason) {
      return readableReason[reason] || this.$t('pages.lead.conversions.noValue')
    },
    getReadableTopic (topic, topicsOther) {
      if (topic === 'OTHER' && topicsOther.length > 0) {
        return topicsOther
      }
      return readableTopic[topic]
    },
    getValuationPreviewUrl (conversion) {
      return conversion?.valuation?.pdf?.file?.cdnUrl
    },

    showModal (type) {
      if (modals[type]) {
        this.$modal.show(
          modals[type],
          {
            leadId: this.lead.id,
            companyId: this.lead.company.id
          },
          {
            height: 'auto'
          }
        )
      }
    }

  }
}
</script>
